import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Button, Card, Chip, DChip, DataTable, EmptyState, Icon, Modal, Pane, PaneHead, PhoneCards, PhoneScreen, PickerChip, TabBar,
} from '@/components/kit';
import type { TableRow } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useAuthStore } from '@/stores/useAuthStore';
import { Permission } from '@/types/user';
import { ItemFields } from './ItemFields';
import { draftOf, type Draft } from './lib/itemDraft';
import {
  dayMonth,
  majorOf,
  minorOf,
  useCatalog,
  useHolderCandidates,
  useReassignHolder,
  useSaveItem,
  useShelf,
  useStockHolder,
  useSuppliers,
  type CatalogItem,
} from './lib/useStock';
import { StockMenu } from './StockMenu';

/**
 * Stock · Item catalog — `g_catalog` (f2) and its deactivated tab (f2b).
 *
 * DEFINITIONS LIVE APART FROM QUANTITIES. This is deliberately not the shelf:
 * it answers "what does this clinic buy?", lists every item whether or not
 * anything is on the shelf, and shows no expiry and no rate. A nurse never
 * defines an item — she finds it already there — which is why the two screens
 * are separate rather than one list with an edit mode.
 *
 * NOTHING IS EVER DELETED. Removing an item deactivates it, because old
 * movements, orders and CONSUMES lists would lose their meaning otherwise. A
 * deactivated item keeps its history, lands on the second tab with the date,
 * the person and what it still had on hand, and its removal tells the clinic's
 * managers — the two numbers that drive reorder drafts and the value of a
 * write-off never change quietly.
 */

const EMPTY: CatalogItem[] = [];

/**
 * "Held by Hoda Samir · one holder per branch" and its Reassign. The handover
 * sends the version of the holder this screen saw; if someone else moved it
 * first the server refuses and we re-read instead of overwriting them.
 */
function HolderLine({ canWrite, phone = false }: { canWrite: boolean; phone?: boolean }) {
  const { data } = useStockHolder();
  const { data: people = [] } = useHolderCandidates(canWrite);
  const reassign = useReassignHolder();
  if (!data?.holder) return null;
  const holder = data.holder;
  const first = holder.name.split(' ');
  const short = phone && first.length > 1 ? `${first[0]} ${first[1][0]}.` : holder.name;
  const options = people
    .filter((p) => p.userId !== holder.userId)
    .map((p) => ({ value: p.userId, label: `${p.name} · ${p.role}` }));
  const move = (toUserId: string) =>
    reassign.mutate(
      { toUserId, expectedUserId: holder.userId },
      {
        onSuccess: (r) => toast.success(`${r.holder?.name ?? 'They'} now hold${r.holder ? 's' : ''} stock.`),
        onError: () => toast.error('Someone else changed who holds stock — it was reloaded, try again.'),
      },
    );
  return (
    <div className="flex mt8" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
      <p className="t-sec mut" style={{ margin: 0 }}>
        {phone
          ? `Held by: ${short} · reassigning moves it — one holder per branch`
          : `Held by ${holder.name} · one holder per branch — reassigning moves the whole module, not the shelf.`}
      </p>
      {canWrite && options.length ? (
        <PickerChip label="Reassign ▾" ariaLabel="Reassign who holds stock" options={options} onChange={move} menuAlign="right" />
      ) : null}
    </div>
  );
}

export function StockCatalogPage() {
  const go = useNavigate();
  const { branch } = useShellIdentity();
  const branchName = branch.replace(/\s*▾\s*$/, '');
  const roleCheck = useAuthStore((s) => s.roleCheck);
  const canWrite = roleCheck.hasPermission(Permission.STOCK_WRITE);

  const { data, isLoading, isError } = useCatalog();
  const items = data ?? EMPTY;
  const { data: suppliers } = useSuppliers();
  /* The measured rate lives on the shelf read; the catalog judges an order
     size against it, so the two are shown together here. */
  const { data: shelf } = useShelf();

  const [tab, setTab] = useState<'active' | 'deactivated'>('active');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [trackedOnly, setTracked] = useState(false);
  const [sellsOnly, setSells] = useState(false);
  /* `?edit=<id>` — the shelf's "Edit" lands here with that item picked. */
  const [params] = useSearchParams();
  const [pickedId, setPicked] = useState<string | null>(params.get('edit'));
  const [deactivating, setDeactivating] = useState<CatalogItem | null>(null);

  usePageMeta({ title: 'Item catalog' });

  const active = items.filter((i) => i.isActive);
  const dead = items.filter((i) => !i.isActive);
  const categories = useMemo(
    () => [...new Set(items.map((i) => i.category).filter((c): c is string => !!c))].sort(),
    [items],
  );
  const rateOf = (id: string) => shelf?.find((s) => s.id === id)?.perMonth ?? null;

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (tab === 'active' ? active : dead).filter((i) => {
      if (category && i.category !== category) return false;
      if (trackedOnly && !i.batchTracked) return false;
      if (sellsOnly && !i.sellable) return false;
      const supplier = suppliers?.find((s) => s.id === i.preferredSupplierId)?.name ?? '';
      if (q && !`${i.name} ${i.barcode ?? ''} ${i.category ?? ''} ${supplier}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [tab, active, dead, category, trackedOnly, sellsOnly, query, suppliers]);

  const picked = items.find((i) => i.id === pickedId) ?? null;
  const save = useSaveItem();


  const tableRows: TableRow[] = rows.map((item) => ({
    id: item.id,
    cells: {
      item: (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span className="stock-thumb" aria-hidden="true">
            {item.photoUrl ? <img src={item.photoUrl} alt="" /> : <Icon name="photo" />}
          </span>
          <div style={{ minWidth: 0 }}>
            <p className="t-strong">{item.name}</p>
            <p className="t-cap mut">
              {item.category || 'uncategorised'}
              {item.barcode ? ` · ${item.barcode}` : ''}
            </p>
          </div>
        </div>
      ),
      unit: <span className="mut">{item.unit || '—'}</span>,
      min: <span className="mut">{item.minLevel ?? '—'}</span>,
      /* THE TWO COSTS ARE NOT THE SAME NUMBER. The expected cost is the
         item's own; the last-paid is whatever the most recent invoice
         charged. Showing one silently in the other's place is how the edit
         field ends up blank beside a figure the table just printed — so an
         unset expected cost prints the last-paid, marked as such. */
      cost:
        item.standardCostMinor != null ? (
          <span className="mut num">{majorOf(item.standardCostMinor)}</span>
        ) : item.lastCostMinor != null ? (
          /* `≈` is the boards' own mark for a derived figure (the shelf's
             "≈ / MO"), and it fits a 9% column where "last paid" wrapped to
             three lines. The pane spells it out. */
          <span className="mut num" title="what the last delivery charged — no expected cost is set">
            ≈ {majorOf(item.lastCostMinor)}
          </span>
        ) : (
          <span className="mut">—</span>
        ),
      sells: item.sellable ? (
        <span className="t-strong brand">Sells at the desk</span>
      ) : (
        <span className="mut">—</span>
      ),
      batches: item.batchTracked ? (
        <Chip kind="soft" small>tracked</Chip>
      ) : (
        <span className="mut">—</span>
      ),
      act: canWrite ? <DChip onClick={() => setPicked(item.id)}>Edit</DChip> : null,
    },
  }));

  /* `g_k2`: a card is the definition in one line — unit, tracking, minimum,
     cost — with Edit on its right. */
  const phoneRows: TableRow[] = rows.map((item) => ({
    id: item.id,
    cells: {
      item: (
        <div style={{ minWidth: 0 }}>
          <p className="t-strong">{item.name}</p>
          <p className="t-cap mut">
            {[
              item.unit || null,
              item.batchTracked ? 'batch-tracked' : 'no batches',
              item.minLevel != null ? `min ${item.minLevel}` : null,
              item.standardCostMinor != null ? `EGP ${majorOf(item.standardCostMinor)}` : null,
            ].filter(Boolean).join(' · ')}
          </p>
        </div>
      ),
      act: canWrite ? <DChip onClick={() => setPicked(item.id)}>Edit</DChip> : null,
    },
  }));

  useShellChrome(
    {
      raw: true,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!picked,
      onPaneClose: () => setPicked(null),
      paneMenu: (close) => <StockMenu door="catalog" onClose={close} />,
      /* `g_k2` — the desk table as cards. The rows are the table's
         own, so a figure or a state can never read differently here. */
      phone: (
        <PhoneScreen
          className="stock-phone"
          crumb="Stock"
          title="Item catalog"
          onBack={() => go('/stock-manager/shelf')}
          trailing={
            canWrite ? (
              <Chip kind="brand" small className="chip-act w6"
                onClick={() => go('/stock-manager/catalog/new')}>+ New{active.length ? ` → ${active.length}` : ''}</Chip>
            ) : undefined
          }
          menu={(close) => <StockMenu door="catalog" onClose={close} />}
        >
          <p className="t-sec mut mb8">
            The items THIS clinic actually buys — definitions, not quantities.
          </p>
          <PhoneCards
            columns={[
              { key: 'item', label: 'Item', width: 80 },
              { key: 'act', label: '', width: 20 },
            ]}
            rows={phoneRows}
            body="item"
            trailing="act"
            onOpen={(r) => setPicked(r.id)}
            empty={<EmptyState title="Nothing in the catalog" sub="An item is a definition — what it is and what it costs. Quantities live on the shelf." />}
          />
          <HolderLine canWrite={canWrite} phone />
          {canWrite ? (
            <button type="button" className="card card-btn brand t-strong"
              style={{ borderColor: 'var(--brand-300, var(--brand-600))', background: 'var(--brand-50, transparent)' }}
              onClick={() => go('/stock-manager/catalog/new')}>
              + New item — opens the full editor
            </button>
          ) : null}
        </PhoneScreen>
      ),
      title: 'Item catalog',
      sub: isLoading
        ? 'reading the catalog…'
        : `what this clinic actually buys · ${active.length} item${active.length === 1 ? '' : 's'}`,
      search: 'Search items, barcodes or suppliers',
      searchValue: query,
      onSearchChange: setQuery,
      ...(canWrite
        ? { primary: { label: '+ New item', onClick: () => go('/stock-manager/catalog/new') } }
        : {}),
      pane: picked ? (
        <EditPane
          key={picked.id}
          item={picked}
          branchName={branchName}
          canWrite={canWrite}
          suppliers={suppliers ?? []}
          categories={categories}
          perMonth={rateOf(picked.id)}
          onClose={() => setPicked(null)}
          onDeactivate={() => setDeactivating(picked)}
          onReactivate={() => {
            save.mutate(
              { id: picked.id, name: picked.name, isActive: true },
              { onSuccess: () => toast.success(`${picked.name} is back in the catalog`) },
            );
          }}
          onSave={(draft) =>
            save.mutate(
              {
                id: picked.id,
                name: draft.name.trim(),
                category: draft.category,
                unit: draft.unit,
                barcode: draft.barcode?.trim() || null,
                minLevel: draft.min.trim() === '' ? null : Number(draft.min),
                batchTracked: !!draft.batchTracked,
                standardCostMinor: minorOf(draft.cost),
                preferredSupplierId: draft.preferredSupplierId,
                autoOrderEnabled: !!draft.autoOrderEnabled,
                autoOrderThreshold: draft.threshold.trim() === '' ? null : Number(draft.threshold),
                autoOrderQuantity: draft.orderQty.trim() === '' ? null : Number(draft.orderQty),
              },
              {
                onSuccess: () => toast.success(`${draft.name.trim()} saved`),
                onError: () => toast.error('The item was not saved. Nothing has been changed.'),
              },
            )
          }
        />
      ) : (
        <Pane
          head={
            tab === 'deactivated' ? (
              <PaneHead overline="Deactivated" title="Nothing picked" />
            ) : (
              <PaneHead
                overline="Edit item"
                title="Nothing picked"
                sub="pick a row to change what this clinic buys"
              />
            )
          }
        >
          <div className="emptystate">
            {tab === 'deactivated' ? (
              <p className="t-sec">
                Pick a deactivated item to see when it was removed, by whom, and what it still had
                on hand.
              </p>
            ) : (
            <p className="t-sec">
              An item’s definition is its name, its unit, what it costs and whether its boxes carry
              dates. What is on the shelf is decided elsewhere — by a receipt, a use or a count.
            </p>
            )}
          </div>
        </Pane>
      ),
    },
    [picked, suppliers, categories, canWrite, query, isLoading, active.length, rows.map((r) => r.id).join(), branchName, shelf, pickedId],
  );

  return (
    <div className="maie desk stock-root" style={{ paddingBottom: 88 }}>
      <div className="filterrow">
        <Chip kind={category === '' ? 'brand' : 'outline'} small onClick={() => setCategory('')}>
          All categories{category === '' ? ' ✓' : ''}
        </Chip>
        {categories.map((c) => (
          <Chip
            key={c}
            kind={category === c ? 'brand' : 'outline'}
            small
            onClick={() => setCategory(category === c ? '' : c)}
          >
            {c}{category === c ? ' ✓' : ''}
          </Chip>
        ))}
        <Chip kind={trackedOnly ? 'brand' : 'outline'} small onClick={() => setTracked((v) => !v)}>
          Batch-tracked{trackedOnly ? ' ✓' : ''}
        </Chip>
        <Chip kind={sellsOnly ? 'brand' : 'outline'} small onClick={() => setSells((v) => !v)}>
          Sells at the desk{sellsOnly ? ' ✓' : ''}
        </Chip>
      </div>

      <div className="dcontent scroll" style={{ paddingBottom: 72 }}>
        {/* In the content column: the kit gives each tab `flex:1`, which only
            divides a full-width strip. */}
        <TabBar
          label="Catalog"
          items={[
            { key: 'active' as const, label: 'Active' },
            {
              key: 'deactivated' as const,
              label: `Deactivated${dead.length ? ` · ${dead.length}` : ''}`,
            },
          ]}
          active={tab}
          onSelect={(k) => {
            setTab(k);
            setPicked(null);
          }}
        />
        <DataTable
          caption={tab === 'active' ? 'What this clinic buys' : 'Items no longer bought'}
          columns={[
            { key: 'item', label: 'Item', width: 46 },
            { key: 'unit', label: 'Unit', width: 10 },
            { key: 'min', label: 'Min', width: 6 },
            { key: 'cost', label: 'Cost', width: 9 },
            { key: 'sells', label: 'Sells', width: 15 },
            { key: 'batches', label: 'Batches', width: 8 },
            { key: 'act', label: '', width: 6 },
          ]}
          rows={tableRows}
          selectedId={pickedId}
          onSelect={(r) => setPicked(r.id === pickedId ? null : r.id)}
          empty={
            <div className="emptystate">
              {isLoading ? (
                <p className="t-sec mut">Reading the catalog…</p>
              ) : isError ? (
                <p className="t-sec mut">The catalog did not come back. Nothing has been changed.</p>
              ) : tab === 'deactivated' ? (
                <>
                  <p className="es-t">Nothing is deactivated</p>
                  <p className="t-sec mut">
                    Removing an item deactivates it — MAIE never deletes one, because old
                    movements, orders and CONSUMES lists would lose their meaning. A deactivated
                    item lands here with the date, the person and what it still had on hand.
                  </p>
                  <Button kind="outline" onClick={() => setTab('active')}>
                    Back to the active items
                  </Button>
                </>
              ) : items.length === 0 ? (
                <>
                  <p className="es-t">The catalog is empty</p>
                  <p className="t-sec mut">
                    An invoice can only be received against something the clinic says it buys, so
                    this is where the shelf starts.
                  </p>
                  {canWrite ? (
                    <p className="mt12">
                      <DChip kind="brand" onClick={() => go('/stock-manager/catalog/new')}>
                        + New item
                      </DChip>
                    </p>
                  ) : null}
                </>
              ) : (
                <>
                  <p className="es-t">No item matches</p>
                  <p className="t-sec mut">Clear the filters to see all {active.length} of them.</p>
                </>
              )}
            </div>
          }
        />

        {tab === 'active' ? (
          <p className="t-sec mut mt16">
            Definitions live apart from quantities — nurses never define an item, they just find it
            there. Cost and min-level edits ping the clinic manager: the two numbers that drive
            reorder drafts and the value of a write-off never change quietly.
          </p>
        ) : (
          <p className="t-sec mut mt16">
            {active.length} active item{active.length === 1 ? '' : 's'} · {dead.length} deactivated
            · deactivation is logged and pings the clinic manager.
          </p>
        )}
        <HolderLine canWrite={canWrite} />
      </div>

      <DeactivateModal
        item={deactivating}
        onClose={() => setDeactivating(null)}
        onConfirm={(reason) => {
          if (!deactivating) return;
          save.mutate(
            { id: deactivating.id, name: deactivating.name, isActive: false, deactivatedReason: reason },
            {
              onSuccess: () => {
                toast.success(`${deactivating.name} deactivated — the managers have been told`);
                setPicked(null);
              },
              onError: () => toast.error('It was not deactivated. Nothing has been changed.'),
            },
          );
          setDeactivating(null);
        }}
      />
    </div>
  );
}

/** The docked editor — f2's pane, which is the same nine facts as f3. */
function EditPane({
  item,
  branchName,
  canWrite,
  suppliers,
  categories,
  perMonth,
  onClose,
  onSave,
  onDeactivate,
  onReactivate,
}: {
  item: CatalogItem;
  branchName: string;
  canWrite: boolean;
  suppliers: Parameters<typeof ItemFields>[0]['suppliers'];
  categories: string[];
  perMonth: number | null;
  onClose: () => void;
  onSave: (draft: Draft) => void;
  onDeactivate: () => void;
  onReactivate: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(item));
  const dirty = JSON.stringify(draft) !== JSON.stringify(draftOf(item));

  return (
    <Pane
      onClose={onClose}
      head={
        <PaneHead
          overline={item.isActive ? 'Edit item' : 'Deactivated item'}
          title={item.name}
          sub={`${item.unit || item.category || 'item'} · ${
            item.batchTracked ? 'batch-tracked' : 'no batches'
          }`}
          wrapTitle
        />
      }
      foot={
        canWrite ? (
          item.isActive ? (
            <>
              <button
                type="button"
                className="btn btn-warnline"
                style={{ flex: 1, minHeight: 44 }}
                onClick={onDeactivate}
              >
                Deactivate
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ flex: 1, minHeight: 44 }}
                disabled={!dirty || !draft.name.trim()}
                onClick={() => onSave(draft)}
              >
                Save changes
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-primary grow"
              style={{ minHeight: 44 }}
              onClick={onReactivate}
            >
              Put it back in the catalog
            </button>
          )
        ) : undefined
      }
    >
      {!item.isActive ? (
        <Card style={{ borderColor: 'var(--warning-600)' }}>
          <p className="t-strong">
            Deactivated{item.deactivatedAt ? ` ${dayMonth(item.deactivatedAt)}` : ''}
            {item.deactivatedBy ? ` by ${item.deactivatedBy}` : ''}
          </p>
          {item.deactivatedReason ? (
            <p className="t-sec sec mt4">{item.deactivatedReason}</p>
          ) : null}
          <p className="t-cap mut mt8">
            {item.inStock > 0
              ? `${item.inStock} still on the shelf at ${branchName} — it can be counted and written off, but not ordered.`
              : 'Nothing is on the shelf. Its history is kept either way.'}
          </p>
        </Card>
      ) : null}

      <ItemFields
        compact
        draft={draft}
        onChange={(next) => setDraft((d) => ({ ...d, ...next }))}
        suppliers={suppliers}
        categories={categories}
        perMonth={perMonth}
        lastPaid={item.lastCostMinor != null ? majorOf(item.lastCostMinor) : undefined}
      />

      <Card>
        <p className="t-ovl mut">Sold at the desk</p>
        <p className="t-strong mt8">
          {item.sellable ? 'Sells at the desk' : 'Not sold — consumable only'}
        </p>
        <p className="t-cap mut mt8">
          a product is a stock item with a sell price — it receives, counts and reorders like a
          consumable, and selling one decrements this shelf. The price and the tax class live in
          Reception’s catalog.
        </p>
      </Card>

      {item.lastCostMinor != null ? (
        <Card>
          <p className="t-ovl mut">Last paid</p>
          <p className="t-strong mt8">{majorOf(item.lastCostMinor)} / {item.unit || 'unit'}</p>
          <p className="t-cap mut mt8">
            {item.standardCostMinor != null && item.standardCostMinor !== item.lastCostMinor
              ? `the expected cost above says ${majorOf(item.standardCostMinor)} — the last invoice disagreed`
              : 'what the most recent delivery actually charged'}
          </p>
        </Card>
      ) : null}
    </Pane>
  );
}

/** Removing an item is a decision somebody else hears about, so it asks why. */
function DeactivateModal({
  item,
  onClose,
  onConfirm,
}: {
  item: CatalogItem | null;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');

  return (
    <Modal
      open={!!item}
      onClose={onClose}
      title={item ? `Deactivate ${item.name}?` : 'Deactivate'}
      sub="it leaves every future order, count and CONSUMES list"
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Keep it</button>
          <button
            type="button"
            className="btn btn-warnline"
            onClick={() => {
              onConfirm(reason.trim());
              setReason('');
            }}
          >
            Deactivate
          </button>
        </>
      }
    >
      {item ? (
        <>
          <p className="t-sec sec">
            Nothing is deleted. Every past movement, order and receipt keeps its meaning, and the
            item can be put back at any time.
          </p>
          {item.inStock > 0 ? (
            <Card style={{ borderColor: 'var(--warning-600)' }}>
              <p className="t-strong">{item.inStock} still on the shelf</p>
              <p className="t-cap mut mt8">
                it can still be counted and written off — it just cannot be ordered again
              </p>
            </Card>
          ) : null}
          <label className="t-ovl mut mt12" htmlFor="deact-why">Why</label>
          <input
            id="deact-why"
            className="fld t-strong mt4"
            placeholder="switched supplier · no longer used · duplicate"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <p className="t-cap mut mt8">
            This goes to the clinic’s managers with your name against it.
          </p>
        </>
      ) : null}
    </Modal>
  );
}
